/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import * as Plotly from 'plotly.js-dist-min';
import { Config, Data, Layout } from 'plotly.js-dist-min';
import { fromEvent, debounceTime, Subject, takeUntil } from 'rxjs';
import {
  IOxygenCalibrationDevice,
  IOxygenCalibrationFlask,
  IOxygenValue,
} from 'src/app/_models/experiment-data';
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';

@Component({
  selector: 'app-oxygen-calibration-graph',
  templateUrl: './oxygen-calibration-graph.component.html',
  styleUrls: ['./oxygen-calibration-graph.component.scss'],
  imports: [MatSlideToggle, MatIconButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OxygenCalibrationGraphComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  readonly plotlyDiv = viewChild<ElementRef>('plotlyDiv');

  private readonly destroy$ = new Subject<void>();

  private readonly flaskColours: Record<string, string> = {
    A: '#6ECF9C',
    B: '#6C73C1',
    C: '#BE4FC1',
    D: '#CB9F50',
  };

  // Graph state
  checked = signal(false);
  dragMode = signal<'zoom' | 'pan'>('zoom');
  private graphReady = false;
  private readonly initialRadius = 6;

  // Point magnification settings (matching D3 behavior)
  private readonly minMarkerSize = 2;
  private readonly maxMarkerSize = 12;
  private baseMarkerSize = 6; // Set dynamically based on dataset size
  private initialXRange: [number, number] | null = null;
  private initialYRange: [number, number] | null = null;

  private readonly margin = {
    left: 90,
    right: 5,
    top: 10,
    bottom: 80,
  };

  private width = 800;
  private height = 450;
  private readonly maxHeight = 450;

  // Data state
  private dataToRender: IRenderData[] = [];
  private labelList: IPropertyColour[] = [];
  private errorMessage: string | null = null;

  // Signal inputs
  readonly graphId = input('scatter');
  readonly graphTitle = input('Title Here');
  readonly yAxisLabel = input('Y Axis Label');
  readonly dataType = input('Oxygen');
  readonly calibrationData = input<IOxygenCalibrationDevice>();

  ngAfterViewInit(): void {
    this.initialiseGraph();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('calibrationData' in changes) {
      const change = changes['calibrationData'];

      if (change.firstChange) {
        return;
      }

      if (!change.currentValue) {
        return;
      }

      this.dataToRender = [];
      this.labelList = [];
      this.parseData(change.currentValue, true);
    }
  }

  /**
   * Safely execute Plotly operations with error handling
   */
  private safePlotlyOperation(operation: () => void): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) {
      return;
    }
    try {
      operation();
    } catch (error) {
      this.errorMessage = error;
    }
  }

  /**
   * Calculate zoom scale and update marker sizes (matching D3 point magnification)
   */
  private updateMarkerSizeOnZoom(): void {
    if (
      !this.graphReady ||
      !this.plotlyDiv()?.nativeElement ||
      !this.initialXRange ||
      !this.initialYRange
    ) {
      return;
    }

    const layout = this.plotlyDiv().nativeElement.layout;
    const currentXRange = layout.xaxis?.range;
    const currentYRange = layout.yaxis?.range;

    if (!currentXRange || !currentYRange) return;

    // Calculate zoom scale based on how much the axis range has shrunk
    const initialXSpan = this.initialXRange[1] - this.initialXRange[0];
    const initialYSpan = this.initialYRange[1] - this.initialYRange[0];
    const currentXSpan = currentXRange[1] - currentXRange[0];
    const currentYSpan = currentYRange[1] - currentYRange[0];

    // Use the average of X and Y zoom scales
    const xZoomScale = initialXSpan / currentXSpan;
    const yZoomScale = initialYSpan / currentYSpan;
    const zoomScale = (xZoomScale + yZoomScale) / 2;

    // Calculate new marker size: scale linearly with zoom but clamp to min/max
    const scaledSize = this.baseMarkerSize * zoomScale;
    const newSize = Math.min(
      Math.max(this.minMarkerSize, scaledSize),
      this.maxMarkerSize,
    );

    // Update all traces with the new marker size
    const traceCount = this.plotlyDiv().nativeElement.data?.length || 0;
    if (traceCount > 0) {
      const traceIndices = Array.from({ length: traceCount }, (_, i) => i);
      try {
        Plotly.restyle(
          this.plotlyDiv().nativeElement,
          { 'marker.size': newSize },
          traceIndices,
        );
      } catch (error) {
        // Silently fail - marker size update is not critical
      }
    }
  }

  /**
   * Clear any active tooltips/hover state
   */
  private clearTooltips(): void {
    if (this.plotlyDiv()?.nativeElement) {
      try {
        Plotly.Fx.hover(this.plotlyDiv().nativeElement, []);
      } catch {
        // Silently fail - tooltip clearing is not critical
      }
    }
  }

  toggleChange(event: MatSlideToggleChange): void {
    this.checked.set(event.checked);

    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) {
      return;
    }

    const currentLayout = this.plotlyDiv()?.nativeElement?.layout;
    const currentXRange = currentLayout?.xaxis?.range;
    const currentYRange = currentLayout?.yaxis?.range;

    const update: Partial<Layout> = {
      xaxis: {
        ...this.getXAxisConfig(),
        range: currentXRange,
        fixedrange: this.checked(),
      },
      yaxis: {
        ...this.getYAxisConfig(),
        range: currentYRange,
        fixedrange: this.checked(),
      },
    };

    try {
      Plotly.relayout(this.plotlyDiv().nativeElement, update);
    } catch {
      // Silently fail
    }
  }

  resetZoom(): void {
    const { minX, maxX, minY, maxY } = this.getDataBounds();

    const yPadding = (maxY - minY) * 0.1;

    const update: Partial<Layout> = {
      'xaxis.range': [minX, maxX],
      'yaxis.range': [minY - yPadding, maxY + yPadding],
    };

    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    try {
      Plotly.animate(
        this.plotlyDiv().nativeElement,
        { layout: update },
        {
          transition: {
            duration: 750,
            easing: 'cubic-in-out',
          },
          frame: {
            duration: 750,
          },
        },
      );
    } catch {
      // Silently fail
    }
  }

  zoomIn(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const layout = this.plotlyDiv().nativeElement.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    const xCenter = (xRange[0] + xRange[1]) / 2;
    const yCenter = (yRange[0] + yRange[1]) / 2;
    const xHalf = (xRange[1] - xRange[0]) / 4;
    const yHalf = (yRange[1] - yRange[0]) / 4;

    this.safePlotlyOperation(() => {
      Plotly.relayout(this.plotlyDiv().nativeElement, {
        'xaxis.range': [xCenter - xHalf, xCenter + xHalf],
        'yaxis.range': [yCenter - yHalf, yCenter + yHalf],
      });
      this.clearTooltips();
    });
  }

  zoomOut(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const layout = this.plotlyDiv().nativeElement.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    const xCenter = (xRange[0] + xRange[1]) / 2;
    const yCenter = (yRange[0] + yRange[1]) / 2;
    const xHalf = xRange[1] - xRange[0];
    const yHalf = yRange[1] - yRange[0];

    this.safePlotlyOperation(() => {
      Plotly.relayout(this.plotlyDiv().nativeElement, {
        'xaxis.range': [xCenter - xHalf, xCenter + xHalf],
        'yaxis.range': [yCenter - yHalf, yCenter + yHalf],
      });
      this.clearTooltips();
    });
  }

  setDragMode(mode: 'zoom' | 'pan'): void {
    this.dragMode.set(mode);
    this.safePlotlyOperation(() =>
      Plotly.relayout(this.plotlyDiv().nativeElement, { dragmode: mode }),
    );
  }

  downloadImage(): void {
    this.safePlotlyOperation(() =>
      Plotly.downloadImage(this.plotlyDiv().nativeElement, {
        format: 'png',
        filename: 'oxygen-calibration-graph',
        width: this.width + this.margin.left + this.margin.right,
        height: this.height + this.margin.top + this.margin.bottom,
      }),
    );
  }

  private readonly initialiseGraph = (): void => {
    const data = this.calibrationData();
    if (data) {
      this.parseData(data);
    }

    const resizeObservable$ = fromEvent(window, 'resize').pipe(
      debounceTime(300),
    );

    resizeObservable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleResize();
    });
  };

  private readonly parseData = (
    calibrationData: IOxygenCalibrationDevice,
    isUpdate = false,
  ): void => {
    const property = this.dataType();

    calibrationData.flasks.forEach((flask: IOxygenCalibrationFlask) => {
      const flaskLetter = flask.name.charAt(0);
      const deviceFlaskProperty = `${flask.name}_${property}`;

      flask.values?.forEach((calibrationValue: IOxygenValue) => {
        this.dataToRender.push({
          DeviceFlaskProperty: deviceFlaskProperty,
          OxygenDphi: calibrationValue.dphi,
          Sample: calibrationValue.sample,
          FlaskName: flask.name,
        });
      });

      this.labelList.push({
        deviceFlaskProperty,
        colour: this.flaskColours[flaskLetter],
      });
    });

    if (isUpdate) {
      this.updateGraph();
    } else {
      this.drawGraph();
    }
  };

  private getDataBounds(): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } {
    if (this.dataToRender.length === 0) {
      return { minX: 0, maxX: 10, minY: 0, maxY: 1 };
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const point of this.dataToRender) {
      if (point.Sample < minX) minX = point.Sample;
      if (point.Sample > maxX) maxX = point.Sample;
      if (point.OxygenDphi < minY) minY = point.OxygenDphi;
      if (point.OxygenDphi > maxY) maxY = point.OxygenDphi;
    }

    // Ensure minimum x range for sample data
    minX = 0;
    maxX = Math.max(10, maxX);

    // Handle edge cases
    if (minY === maxY) maxY = minY + 1;

    return { minX, maxX, minY, maxY };
  }

  private getXAxisConfig(): Partial<Layout['xaxis']> {
    const { minX, maxX } = this.getDataBounds();

    return {
      title: {
        text: 'Sample',
        font: {
          weight: 600,
          family: 'figtree',
          size: 18,
        },
      },
      range: [minX, maxX],
      fixedrange: this.checked(),
      showgrid: true,
      zeroline: false,
      tickmode: 'auto',
      dtick: 1,
    };
  }

  private getYAxisConfig(): Partial<Layout['yaxis']> {
    const { minY, maxY } = this.getDataBounds();
    const yPadding = (maxY - minY) * 0.1;

    return {
      title: {
        text: this.yAxisLabel(),
        font: {
          weight: 600,
          family: 'figtree',
          size: 18,
        },
      },
      range: [minY - yPadding, maxY + yPadding],
      fixedrange: this.checked(),
      showgrid: true,
      zeroline: false,
      tickmode: 'auto',
    };
  }

  private readonly drawGraph = (): void => {
    // Calculate dimensions
    const containerWidth = this.plotlyDiv()?.nativeElement?.offsetWidth || 800;
    this.width = containerWidth - (this.margin.left + this.margin.right);
    const currentHeight = this.width / 1.8;
    this.height = Math.min(currentHeight, this.maxHeight);

    // Group data by DeviceFlaskProperty
    const groupedData = new Map<string, IRenderData[]>();
    this.dataToRender.forEach((point) => {
      if (!groupedData.has(point.DeviceFlaskProperty)) {
        groupedData.set(point.DeviceFlaskProperty, []);
      }
      groupedData.get(point.DeviceFlaskProperty)?.push(point);
    });

    // Create scatter traces
    const traces: Data[] = [];

    Array.from(groupedData.entries()).forEach(([key, points]) => {
      const label = this.labelList.find((l) => l.deviceFlaskProperty === key);
      const color = label?.colour || '#000000';
      const flaskName = points[0]?.FlaskName || key;
      const flaskLetter = flaskName.charAt(0);

      traces.push({
        x: points.map((p) => p.Sample),
        y: points.map((p) => p.OxygenDphi),
        mode: 'markers',
        type: 'scatter',
        name: `Flask ${flaskLetter}`,
        marker: {
          color: color,
          size: this.initialRadius,
        },
        hovertemplate:
          '<b>DPHI:</b> %{y}<br>' +
          '<b>Sample:</b> %{x}<br>' +
          `<b>Flask:</b> ${flaskName}` +
          '<extra></extra>',
      } as Data);
    });

    const layout: Partial<Layout> = {
      width: this.width + this.margin.left + this.margin.right,
      height: this.height + this.margin.top + this.margin.bottom,
      xaxis: this.getXAxisConfig(),
      yaxis: this.getYAxisConfig(),
      showlegend: true,
      legend: {
        x: 0.5,
        y: -0.2,
        xanchor: 'center',
        yanchor: 'top',
        orientation: 'h',
        bgcolor: 'rgba(255, 255, 255, 0.8)',
        font: {
          family: 'figtree',
          size: 12,
        },
      },
      margin: {
        l: this.margin.left,
        r: this.margin.right,
        t: this.margin.top,
        b: this.margin.bottom,
      },
      hoverlabel: {
        bgcolor: '#B4DFFF',
        bordercolor: '#0C7BB4',
        font: {
          family: 'figtree',
          size: 13,
        },
      },
      dragmode: 'zoom',
      hovermode: 'closest',
    };

    const config: Partial<Config> = {
      scrollZoom: true,
      displayModeBar: false,
      displaylogo: false,
      responsive: true,
      doubleClick: 'reset',
    };

    // try {
    //   Plotly.newPlot(this.plotlyDiv().nativeElement, traces, layout, config);
    //   this.graphReady = true;
    // } catch {
    //   this.graphReady = false;
    // }
    try {
      Plotly.newPlot(this.plotlyDiv().nativeElement, traces, layout, config);
      this.graphReady = true;
      this.errorMessage = null;

      // Store initial axis ranges for zoom scale calculation
      const initialLayout = this.plotlyDiv().nativeElement.layout;
      this.initialXRange = initialLayout.xaxis?.range
        ? ([...initialLayout.xaxis.range] as [number, number])
        : null;
      this.initialYRange = initialLayout.yaxis?.range
        ? ([...initialLayout.yaxis.range] as [number, number])
        : null;

      // Handle layout changes (pan, zoom, etc.)
      this.plotlyDiv().nativeElement.on('plotly_relayout', () => {
        this.clearTooltips();
        this.updateMarkerSizeOnZoom();
      });
    } catch (error) {
      this.graphReady = false;
      this.errorMessage =
        'Failed to render graph. Please try refreshing the page.';
    }
  };

  private updateGraph(): void {
    if (!this.plotlyDiv()?.nativeElement) return;

    // Group data by DeviceFlaskProperty
    const groupedData = new Map<string, IRenderData[]>();
    this.dataToRender.forEach((point) => {
      if (!groupedData.has(point.DeviceFlaskProperty)) {
        groupedData.set(point.DeviceFlaskProperty, []);
      }
      groupedData.get(point.DeviceFlaskProperty)?.push(point);
    });

    // Create scatter traces
    const traces: Data[] = [];

    Array.from(groupedData.entries()).forEach(([key, points]) => {
      const label = this.labelList.find((l) => l.deviceFlaskProperty === key);
      const color = label?.colour || '#000000';
      const flaskName = points[0]?.FlaskName || key;
      const flaskLetter = flaskName.charAt(0);

      traces.push({
        x: points.map((p) => p.Sample),
        y: points.map((p) => p.OxygenDphi),
        mode: 'markers',
        type: 'scatter',
        name: `Flask ${flaskLetter}`,
        marker: {
          color: color,
          size: this.initialRadius,
        },
        hovertemplate:
          '<b>DPHI:</b> %{y}<br>' +
          '<b>Sample:</b> %{x}<br>' +
          `<b>Flask:</b> ${flaskName}` +
          '<extra></extra>',
      } as Data);
    });

    const layout: Partial<Layout> = {
      xaxis: this.getXAxisConfig(),
      yaxis: this.getYAxisConfig(),
    };

    try {
      Plotly.react(this.plotlyDiv().nativeElement, traces, layout);
    } catch {
      // Silently fail
    }
  }

  private handleResize(): void {
    if (!this.plotlyDiv()?.nativeElement) return;

    const rect = this.plotlyDiv().nativeElement.getBoundingClientRect();
    const containerWidth = rect.width > 0 ? rect.width : 800;

    this.width = containerWidth - (this.margin.left + this.margin.right);
    const currentHeight = this.width / 1.8;
    this.height = Math.min(currentHeight, this.maxHeight);

    const newLayout = {
      width: this.width + this.margin.left + this.margin.right,
      height: this.height + this.margin.top + this.margin.bottom,
    };

    try {
      Plotly.relayout(this.plotlyDiv().nativeElement, newLayout);
    } catch {
      // Silently fail
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.graphReady = false;
    if (this.plotlyDiv()?.nativeElement) {
      try {
        Plotly.purge(this.plotlyDiv().nativeElement);
      } catch {
        // Silently fail on cleanup
      }
    }
  }
}

export interface IRenderData {
  DeviceFlaskProperty: string;
  OxygenDphi: number;
  Sample: number;
  FlaskName: string;
}

export interface IPropertyColour {
  deviceFlaskProperty: string;
  colour: string;
}
